import React, { useRef, useMemo } from 'react';
import PeopleAltIcon              from '@mui/icons-material/PeopleAlt';
import ShowChartIcon              from '@mui/icons-material/ShowChart';
import LayersIcon                 from '@mui/icons-material/Layers';
import LocalFireDepartmentIcon    from '@mui/icons-material/LocalFireDepartment';

import MapboxMap              from '../../atoms/Mapboxmap/Mapboxmap';
import StatCard               from '../../atoms/StatCard/StatCard';
import SimulationInfoOverlay  from '../../atoms/SimulationInfoOverlay/SimulationInfoOverlay';
import ExportGeoJsonButton    from '../../atoms/ExportGeoJsonButton/ExportGeoJsonButton';
import MapControlPanel        from '../../atoms/MapControlPanel/MapControlPanel';
import HierarchyExplorer      from '../../molecules/HierarchyExplorer/HierarchyExplorer';

import useSimulations from '../../../hooks/useSimulations';
import cidLogo        from '../../../assets/images/cid.png';
import './DashboardHome.css';

const DashboardHome = ({
  admins        = [],
  users         = [],
  loadingAdmins = false,
  loadingUsers  = false,
  selectedAdminId,
  onSelectAdmin,
  isWsConnected = false,
  liveLogs      = [],
  onlineCount   = 0,
  currentUser,
}) => {
  const mapRef = useRef(null);

  const {
    // caches séparés
    simsByUser,     // équipe
    mySimsByUser,   // mon historique
    statsByUser,

    selectedSim,
    activeStep,
    totalSteps,
    expandedUserId,

    loadUserSims,      // équipe (id unique)
    loadTeamSimsBulk,  // équipe (bulk, pour le préchargement d'élagage par filtre)
    loadMyFires,        // mon historique

    handleSelectSim,
    handleStepChange,
    clearSimulation,
    setExpandedUserId,
    exportSimulationGeoJSON,

    // filtres équipe
    teamFilters,
    updateTeamFilters,
    resetTeamFilters,

    // filtres mon historique
    myFilters,
    updateMyFilters,
    resetMyFilters,
  } = useSimulations({ mapRef });

  const totalAdmins  = admins.length;
  const onlineAdmins = admins.filter((a) => a.is_online).length;
  const quotaUsed    = admins.reduce((s, a) => s + (a.simulations_count ?? 0), 0);

  // ↓ statsByUser au lieu de simsByUser — indépendant des filtres actifs
  const totalSimsCached = useMemo(() => {
    return Object.values(statsByUser).reduce((acc, entry) => acc + (entry.sims?.length ?? 0), 0);
  }, [statsByUser]);

  const lastSimulationDate = useMemo(() => {
    const allSims = Object.values(statsByUser).flatMap(entry => entry.sims || []);
    if (!allSims.length) return null;
    const latest = allSims.reduce((latest, current) =>
      new Date(current.fire.created_at) > new Date(latest.fire.created_at) ? current : latest
    );
    return latest.fire.created_at;
  }, [statsByUser]);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  return (
    <div className="dh-root">
      <div className="dh-top">

        <div className="dh-map-wrap">
          <MapboxMap
            ref={mapRef}
            style="mapbox://styles/mapbox/satellite-streets-v12"
          />

          {/* Logo CID */}
          <div className="dh-map-logo">
            <img src={cidLogo} alt="CID" />
          </div>

          {/* Panneau de contrôle 3D — toujours visible, haut droite */}
          <MapControlPanel mapRef={mapRef} />

          {/* Export GeoJSON — visible uniquement si une simulation est sélectionnée */}
          {selectedSim && (
            <div className="dh-export-wrap">
              <ExportGeoJsonButton onExport={exportSimulationGeoJSON} />
            </div>
          )}

          {selectedSim && (
            <SimulationInfoOverlay
              simulation={selectedSim}
              onClose={clearSimulation}
              activeStep={activeStep}
              totalSteps={totalSteps}
              onStepChange={handleStepChange}
            />
          )}
        </div>

        <div className="dh-sidebar">
          <div className="dh-stats">
            <StatCard
              label="Total utilisateurs"
              value={users?.length ?? 0}
              icon={<PeopleAltIcon />}
              accent="#3b82f6"
            />
            <StatCard
              label="Admins en ligne"
              value={onlineAdmins}
              icon={<PeopleAltIcon />}
              accent="#22c55e"
              sub={`sur ${totalAdmins}`}
            />
            <StatCard
              label="Total Simulations"
              value={totalSimsCached}
              icon={<ShowChartIcon />}
              accent="#f59e0b"
            />
            <StatCard
              label="Dernière simulation"
              value={lastSimulationDate ? new Date(lastSimulationDate).toLocaleDateString('fr-FR') : 'N/A'}
              icon={<ShowChartIcon />}
              accent="#f59e0b"
            />
          </div>

          <div className="dh-admins-wrap">
            <HierarchyExplorer
              admins={admins}
              users={users}
              loadingAdmins={loadingAdmins || loadingUsers}
              selectedAdminId={selectedAdminId}
              onSelectAdmin={(admin) => onSelectAdmin?.(admin)}

              // ── Équipe ──
              simsByUser={simsByUser}
              onLoadSims={loadUserSims}
              onLoadTeamSimsBulk={loadTeamSimsBulk}
              teamFilters={teamFilters}
              onTeamFiltersChange={updateTeamFilters}
              onTeamFiltersReset={resetTeamFilters}

              // ── Mon historique ──
              mySimsByUser={mySimsByUser}
              onLoadMyFires={loadMyFires}
              myFilters={myFilters}
              onMyFiltersChange={updateMyFilters}
              onMyFiltersReset={resetMyFilters}

              selectedSimId={selectedSim?.fire?.id}
              expandedUserId={expandedUserId}
              onExpandUser={setExpandedUserId}
              onSelectSim={handleSelectSim}
              currentUserId={currentUser?.id}
              currentUserRole={currentUser?.role}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;